import { el, formatDate, formatTime, isAllDay, groupByDate, voteIcon } from './utils';
import type { EmbedPoll, VoteMap } from './utils';
import { toggleVote } from './vote-toggle';
import { computeCounts, renderResultBar, renderBadges } from './render-shared';

/**
 * Render a time-slot ("when") poll grid.
 * Slots grouped by date with date headers.
 */
export function renderTimePoll(
	poll: EmbedPoll,
	votes: VoteMap,
	onVotesChange: (votes: VoteMap) => void,
	disabled: boolean
): HTMLElement {
	const slots = poll.time_slots || [];
	const settings = poll.settings || {};
	const grouped = groupByDate(slots);

	// Compute vote counts
	const ids = slots.map((s) => s.id);
	const counts = computeCounts(poll, ids);
	const maxVotes = Math.max(...Object.values(counts).map((c) => c.yes + c.maybe + c.no), 1);

	const grid = el('div', { class: 'dte-grid' });

	for (const [, dateSlots] of grouped) {
		// Date header
		const header = el('div', { class: 'dte-date-header' }, [
			'📅 ',
			formatDate(dateSlots[0].start_time)
		]);
		grid.appendChild(header);

		for (const slot of dateSlots) {
			const c = counts[slot.id] || { yes: 0, maybe: 0, no: 0 };
			const currentResponse = votes[slot.id];

			// Vote button
			const btnClass = currentResponse ? `dte-vote-btn dte-vote-btn-${currentResponse}` : 'dte-vote-btn';
			const allDay = isAllDay(slot.start_time, slot.end_time);
			const timeText = allDay ? 'All day' : `${formatTime(slot.start_time)} – ${formatTime(slot.end_time)}`;

			const btn = el('button', {
				class: btnClass,
				type: 'button',
				disabled: disabled,
				'aria-label': `Vote ${currentResponse || 'empty'} for ${timeText}`
			}, [voteIcon(currentResponse)]);

			if (!disabled) {
				btn.addEventListener('click', () => {
					const newVotes = toggleVote(slot.id, votes, settings);
					for (const key of Object.keys(votes)) delete votes[key];
					for (const [k, v] of Object.entries(newVotes)) votes[k] = v;
					onVotesChange(votes);
				});
			}

			// Time label
			const labelClass = allDay ? 'dte-label dte-label-allday' : 'dte-label dte-label-time';
			const label = el('div', { class: labelClass }, [timeText]);

			// Result bar
			const result = renderResultBar(c, maxVotes);

			const row = el('div', { class: 'dte-row' }, [btn, label, result]);
			grid.appendChild(row);

			// Voter badges
			if (!settings.anonymous_voting) {
				const badges = renderBadges(poll, slot.id);
				if (badges) grid.appendChild(badges);
			}
		}
	}

	return grid;
}
